import GoogleCalendarIcon from "@forwardflow/assets/svg/GoogleCalendarIcon"
import { UserService } from "@forwardflow/services/UserService"
import { useAppStore } from "@forwardflow/store"
import { Button, ContentCard, Grid, Stack } from "@forwardflow/ui-v2"
import { useGetUserCalenderInfo } from '@forwardflow/hooks/useGetUserCalenderInfo'
import Icon from '@forwardflow/ui/Icons'
import { Tooltip } from '@mantine/core'
import { motion } from 'framer-motion'
import { ArrowLeftMdIcon, ChevronRightIcon } from '@forwardflow/icons'
import { useOnboardingStoreV2 } from '@forwardflow/store/onboardingStoreV2'
import { useEffect, useRef } from 'react'

export const CalendarConnection = () => {
  const { currentOrganization } = useAppStore()
  const { getNextStep, getPreviousStep, setIsCalendarConnected, isAgent } = useOnboardingStoreV2()
  const { data: UserCalenderInfo } = useGetUserCalenderInfo(
    !!currentOrganization
  )
  const hasAutoAdvanced = useRef(false)

  const isCalendarConnected = !!(UserCalenderInfo && UserCalenderInfo.email)
  const connectedProvider =
    UserCalenderInfo?.provider === 'microsoft' ? 'microsoft' : 'google'
  const providerLabel =
    connectedProvider === 'microsoft' ? 'Outlook Calendar' : 'Google Calendar'

  useEffect(() => {
    if (isCalendarConnected && !hasAutoAdvanced.current) {
      setIsCalendarConnected(true)
      hasAutoAdvanced.current = true
      getNextStep()
    }
  }, [isCalendarConnected, setIsCalendarConnected, getNextStep])

  const handleCalendarConnect = async (
    provider: 'google' | 'microsoft' = 'google'
  ) => {
    try {
      const redirectUrl = encodeURIComponent(
        `${window.location.origin}/onboarding`
      )
      const response =
        provider === 'microsoft'
          ? await UserService.getConnectUserWithOutlookCalendar(
              redirectUrl,
              currentOrganization || ''
            )
          : await UserService.getConnectUserWithCalender(
              redirectUrl,
              currentOrganization || ''
            )

      if (response.redirectUrl) {
        window.location.href = response.redirectUrl
      }
    } catch (e) {
      console.error('Error connecting calendar:', e)
    }
  }

  return (
    <Stack gap={32}>
      <ContentCard 
        className="space-y-6"
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
      >
        {/* Header Section */}
        <div>
          <p className="text-white font-semibold text-lg mb-2">
            {isCalendarConnected
              ? `${providerLabel} Connected`
              : 'Connect Your Calendar'
            }
          </p>
          <p className="text-[#99A1AF] text-sm font-medium max-w-[600px]">
            {isCalendarConnected
              ? `Connected to: ${UserCalenderInfo.email}`
              : 'Connect your Google or Outlook Calendar so Forward Flow can help schedule appointments.'
            }
          </p>
        </div>

        {/* Connect/Change Calendar Button */}
        {isCalendarConnected ? (
          <div className="flex items-center gap-3">
            <button
              onClick={() => handleCalendarConnect(connectedProvider)}
              className="flex items-center h-10 gap-2 px-4 py-2 text-sm font-medium text-white transition-colors rounded-md bg-white/10 hover:bg-white/20"
            >
              {connectedProvider === 'microsoft' ? (
                <Icon name="microsoft-icon" className="w-5" />
              ) : (
                <GoogleCalendarIcon />
              )}
              Change Calendar
            </button>
            <Tooltip
              label={
                <div className="text-xs">
                  Changing your calendar integration won't affect your login account.
                  You'll still sign in the same way, this only updates which calendar
                  Forward Flow uses for scheduling.
                </div>
              }
              position="top"
              multiline
              w={300}
              className="bg-[#1E1E1E] rounded-lg p-4"
            >
              <div className="cursor-pointer">
                <Icon name="Info" width={16} />
              </div>
            </Tooltip>
          </div>
        ) : (
          <div className="flex flex-col sm:flex-row gap-3">
            <button
              onClick={() => handleCalendarConnect('google')}
              className="flex items-center h-10 gap-2 px-4 py-2 font-medium text-white transition-colors rounded-md bg-white/10 hover:bg-white/20"
            >
              <GoogleCalendarIcon />
              Connect Google Calendar
            </button>
            <button
              onClick={() => handleCalendarConnect('microsoft')}
              className="flex items-center h-10 gap-2 px-4 py-2 font-medium text-white transition-colors rounded-md bg-white/10 hover:bg-white/20"
            >
              <Icon name="microsoft-icon" className="w-5" />
              Connect Outlook Calendar
            </button>
          </div>
        )}
      </ContentCard>

      {/* Navigation Buttons */}
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, delay: 0.9 }}
      >
        <Grid>
          <Grid.Col span={3}>
            <Button
              variant="secondary"
              className="w-full"
              disabled={isAgent}
              onClick={() => getPreviousStep()}
            >
              <ArrowLeftMdIcon width={12} /> Back
            </Button>
          </Grid.Col>
          <Grid.Col span={9}>
            <Button
              className="w-full"
              disabled={!isCalendarConnected}
              onClick={() => getNextStep()}
            >
              Continue <ChevronRightIcon width={18} />
            </Button>
          </Grid.Col>
        </Grid>
      </motion.div>
    </Stack>
  )
}